import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from 'react'
import { LICENSE_SCOPE, LicenseScope } from '@saasalerts/common/shared'
import { getLicenseScope } from '@/components/Layout/Header/getLicenseScope'
import { recordMeticulousDomainScope } from '@/utils/meticulous'

interface LicenseScopeContextType {
  licenseScope: LicenseScope | null
}

const LicenseScopeContext = createContext<LicenseScopeContextType | undefined>(
  undefined
)

export const LicenseScopeProvider: React.FC<{
  /*
   * SSR pages that need branding correct in the server-rendered HTML (e.g. /auth,
   * shared reports) resolve the scope from the request host in getServerSideProps
   * and pass it here. Authed pages are statically optimized and omit it, so the
   * scope is resolved on the client below — keeping those pages static is what
   * makes in-app navigation fast (no per-request round-trip).
   */
  initialLicenseScope?: LicenseScope
  children: ReactNode
}> = ({ initialLicenseScope, children }) => {
  const [licenseScope, setLicenseScope] = useState<LicenseScope | null>(
    initialLicenseScope ?? null
  )

  useEffect(() => {
    // Resolve on the client only when the server didn't provide it. Derived from
    // the hostname (one deployment serves every brand domain). Runs once, so the
    // scope stays stable across in-app navigations.
    if (!licenseScope) {
      setLicenseScope(
        getLicenseScope(window.location.hostname) ?? LICENSE_SCOPE.SA
      )
    }
  }, [licenseScope])

  useEffect(() => {
    if (licenseScope) void recordMeticulousDomainScope(licenseScope)
  }, [licenseScope])

  const value = useMemo(() => ({ licenseScope }), [licenseScope])

  return (
    <LicenseScopeContext.Provider value={value}>
      {children}
    </LicenseScopeContext.Provider>
  )
}

export const useLicenseScope = (): LicenseScope => {
  const context = useContext(LicenseScopeContext)
  /*
   * null until the client resolves it (and undefined outside a provider). Falling
   * back to SA keeps the server and first client render identical, so there's no
   * hydration mismatch; the resolved scope arrives on the next render. On authed
   * pages the AuthenticationGuard loader covers that gap, so branding never flashes.
   */
  return context?.licenseScope ?? LICENSE_SCOPE.SA
}
